import { API_BASE } from '@/shared/services/api';
import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { Input } from '@/shared/components/ui/input';
import { Badge } from '@/shared/components/ui/badge';
import { FinanceStatCard } from '@/modules/finance/components/FinanceStatCard';
import { toast } from 'sonner';
import {
  Search,
  Plus,
  Download,
  FileText,
  Receipt,
  Clock,
  AlertTriangle,
  CheckCircle2,
  Send,
  DollarSign,
  RefreshCw } from
'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer } from
'recharts';

const API_URL = API_BASE;

const getAuthHeaders = () => {
  const token = localStorage.getItem('ezerpToken');
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };
};

const statusConfig = {
  draft: { label: 'Draft', class: 'bg-slate-100 text-slate-700' },
  sent: { label: 'Sent', class: 'bg-blue-100 text-blue-700' },
  paid: { label: 'Paid', class: 'bg-emerald-100 text-emerald-700' },
  overdue: { label: 'Overdue', class: 'bg-red-100 text-red-700' },
  partial: { label: 'Partial', class: 'bg-amber-100 text-amber-700' }
};

export const AccountsReceivable = () => {const { t } = useTranslation("finance");
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [invoices, setInvoices] = useState([]);
  const [agingReport, setAgingReport] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [invoicesRes, agingRes] = await Promise.all([
      fetch(`${API_URL}/api/finance/customer-invoices`, { headers: getAuthHeaders() }),
      fetch(`${API_URL}/api/finance/aging-report`, { headers: getAuthHeaders() })]
      );
      if (!invoicesRes.ok || !agingRes.ok) throw new Error('Failed to fetch data');
      const [invoicesData, agingData] = await Promise.all([invoicesRes.json(), agingRes.json()]);
      setInvoices(invoicesData);
      setAgingReport(agingData);
    } catch (err) {
      toast.error('Failed to load accounts receivable data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const stats = {
    totalOutstanding: invoices.filter((i) => i.status !== 'paid').reduce((sum, i) => sum + (i.amount - i.paid_amount), 0),
    overdueAmount: invoices.filter((i) => i.status === 'overdue').reduce((sum, i) => sum + i.amount, 0),
    collectedThisMonth: invoices.filter((i) => i.status === 'paid').reduce((sum, i) => sum + i.amount, 0),
    pendingInvoices: invoices.filter((i) => i.status !== 'paid').length
  };

  const agingData = agingReport ? [
  { name: 'Current', amount: agingReport.current?.amount || 0 },
  { name: '1-30', amount: agingReport.days_30?.amount || 0 },
  { name: '31-60', amount: agingReport.days_60?.amount || 0 },
  { name: '61-90', amount: agingReport.days_90?.amount || 0 },
  { name: '90+', amount: agingReport.over_90?.amount || 0 }] :
  [];

  const filteredInvoices = invoices.filter((invoice) => {
    if (!searchTerm) return true;
    return invoice.id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    invoice.customer_name?.toLowerCase().includes(searchTerm.toLowerCase());
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500" />
      </div>);

  }

  return (
    <div className="space-y-6" data-testid="finance-accounts-receivable">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-['Space_Grotesk']">{t("accountsReceivable", "Accounts Receivable")}</h1>
          <p className="text-muted-foreground mt-1">{t("manageCustomerInvoices", "Manage customer invoices and collections")}

          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchData} className="gap-2">
            <RefreshCw className="h-4 w-4" />{t("refresh", "Refresh")}

          </Button>
          <Button variant="outline" className="gap-2">
            <Download className="h-4 w-4" />{t("exportPdf", "Export PDF")}

          </Button>
          <Button className="gap-2 bg-emerald-600 hover:bg-emerald-700" onClick={() => navigate('/finance/invoices/new')}>
            <Plus className="h-4 w-4" />{t("createInvoice", "Create Invoice")}

          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid gap-4 sm:grid-cols-4">
        <FinanceStatCard
          title="Total Outstanding"
          value={stats.totalOutstanding}
          icon={Receipt}
          color="blue"
          prefix="$" />
        
        <FinanceStatCard
          title="Overdue"
          value={stats.overdueAmount}
          icon={AlertTriangle}
          color="red"
          prefix="$" />
        
        <FinanceStatCard
          title="Collected This Month"
          value={stats.collectedThisMonth}
          icon={CheckCircle2}
          color="emerald"
          prefix="$" />
        
        <FinanceStatCard
          title="Pending Invoices"
          value={stats.pendingInvoices}
          icon={Clock}
          color="gold" />
        
      </div>

      {/* Aging Chart */}
      <Card className="card-elevated">
        <CardHeader>
          <CardTitle className="text-lg">{t("agingReport", "Aging Report")}</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={agingData}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="name" stroke="hsl(var(--muted-foreground))" fontSize={12} />
              <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} tickFormatter={(v) => `$${v / 1000}k`} />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'hsl(var(--card))',
                  border: '1px solid hsl(var(--border))',
                  borderRadius: '8px'
                }}
                formatter={(value) => [`$${value.toLocaleString()}`, 'Amount']} />
              
              <Bar dataKey="amount" fill="#10b981" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Invoices Table */}
      <Card className="card-elevated">
        <CardHeader className="pb-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <CardTitle className="text-lg">{t("customerInvoices", "Customer Invoices")}</CardTitle>
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search invoices..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10" />
              
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-muted/30">
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("invoice", "Invoice #")}</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("customer", "Customer")}</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("invoiceDate", "Invoice Date")}</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("dueDate", "Due Date")}</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">{t("amount", "Amount")}</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">{t("balance", "Balance")}</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("status", "Status")}</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">{t("actions", "Actions")}</th>
                </tr>
              </thead>
              <tbody>
                {filteredInvoices.map((invoice) =>
                <tr key={invoice.id} className="border-b border-border/50 hover:bg-muted/30 transition-colors">
                    <td className="py-4 px-4">
                      <span className="font-mono text-sm font-medium">{invoice.id}</span>
                    </td>
                    <td className="py-4 px-4">
                      <div>
                        <p className="font-medium text-sm">{invoice.customer_name}</p>
                        {invoice.order_reference &&
                      <p className="text-xs text-muted-foreground">{t("order", "Order:")}{invoice.order_reference}</p>
                      }
                      </div>
                    </td>
                    <td className="py-4 px-4 text-sm">{invoice.invoice_date}</td>
                    <td className="py-4 px-4 text-sm">{invoice.due_date}</td>
                    <td className="py-4 px-4 text-right font-mono">
                      ${invoice.amount?.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </td>
                    <td className="py-4 px-4 text-right font-mono font-medium">
                      ${(invoice.amount - invoice.paid_amount)?.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </td>
                    <td className="py-4 px-4">
                      <Badge className={statusConfig[invoice.status]?.class || statusConfig.draft.class}>
                        {statusConfig[invoice.status]?.label || invoice.status}
                      </Badge>
                    </td>
                    <td className="py-4 px-4 text-right">
                      <div className="flex gap-1 justify-end">
                        {invoice.status !== 'paid' &&
                      <>
                            <Button size="sm" variant="outline" className="gap-1" onClick={() => toast.success(`Invoice ${invoice.id} sent!`)}>
                              <Send className="h-3.5 w-3.5" />{t("send", "Send")}

                        </Button>
                            <Button size="sm" variant="outline" className="gap-1" onClick={() => navigate(`/finance/payments/new?invoice=${invoice.id}`)}>
                              <DollarSign className="h-3.5 w-3.5" />{t("recordPayment", "Record Payment")}

                        </Button>
                          </>
                      }
                        {invoice.status === 'paid' &&
                      <Button size="sm" variant="ghost" onClick={() => navigate(`/finance/invoices/${invoice.id}`)}>{t("view", "View")}</Button>
                      }
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {filteredInvoices.length === 0 &&
          <div className="py-12 text-center">
              <Receipt className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">{t("noInvoicesFound", "No invoices found")}</h3>
              <p className="text-muted-foreground">{t("tryAdjustingYour", "Try adjusting your search criteria")}</p>
            </div>
          }
        </CardContent>
      </Card>
    </div>);

};